import React, { useEffect, useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { cx } from './cx';
import { focusRing } from './focusRing';
import Dialog from './Dialog';
import IconButton from './IconButton';
import Money from './Money';
import Skeleton from './Skeleton';
import { playClickSound } from '@/utils/soundUtils';

const FALLBACK_ICON = '/icons/egame.jpg';

export interface GameTileProps {
  name: string;
  imageUrl?: string | null;
  hot?: boolean;
  balance?: number | null;
  footer?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
  /** Skip automatic click sound when onClick is set. */
  silent?: boolean;
  /** Gray icon + label overlaid on the art (Coming soon). */
  comingSoon?: boolean;
  comingSoonLabel?: string;
  /** Warning label over the dimmed art, e.g. "Maintenance" while the provider is down. Replaces "Hot". */
  statusBadge?: string | null;
  /** Text of the "hot" corner badge (translated by the caller). */
  hotLabel?: string;
  /** Card mode: accessible name of the enlarge-art button (translated by the caller). */
  zoomLabel?: string;
  /** Card mode: accessible name of the art preview's close button (translated by the caller). */
  closeLabel?: string;
  /** Enable diagonal tidal-wave effect; tile row in the grid */
  waveRow?: number;
  /** Tile column in the grid — delay = (waveRow + waveCol) × stagger */
  waveCol?: number;
  /**
   * Periodic diagonal mirror shine (Home browse grid). Same gleam as `.btn-shine`,
   * phase-offset by (shineRow + shineCol) so one wavefront sweeps the matrix.
   */
  shineRow?: number;
  shineCol?: number;
  /**
   * Card look: name above the art on a card surface, zoom button for full-size art. Off by
   * default so the icon-tile call sites keep their UI (Home grid, DepositPage picker).
   */
  card?: boolean;
}

/** Placeholder art when a game has no icon: its initials on the raised surface. */
function NebulaArt({ name, className }: { name: string; className?: string }) {
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className={cx('flex h-full w-full items-center justify-center bg-raised', className)}>
      <span className="font-display text-title font-bold tracking-wider text-tertiary">{initials}</span>
    </div>
  );
}

/** Icon art clone of `GameCardArt` (GamesPage): fade in when loaded, fallback image on error. */
function CardArt({ imageUrl, name }: { imageUrl: string; name: string }) {
  const [ready, setReady] = useState(false);
  const [src, setSrc] = useState(imageUrl);
  useEffect(() => {
    setReady(false);
    setSrc(imageUrl);
  }, [imageUrl]);
  return (
    <>
      {!ready && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
      <img
        src={src}
        alt={name}
        draggable={false}
        loading="eager"
        decoding="async"
        className={cx(
          'absolute inset-0 block h-full w-full object-cover pointer-events-none',
          !ready && 'opacity-0'
        )}
        onLoad={() => setReady(true)}
        onError={() => {
          if (src !== FALLBACK_ICON) {
            setSrc(FALLBACK_ICON);
            return;
          }
          setReady(true);
        }}
      />
    </>
  );
}

/** Game icon tile (art + name at the grid's --gi size) with hot / coming-soon / status badges. */
export default function GameTile({
  name,
  imageUrl,
  hot,
  balance,
  footer,
  onClick,
  className,
  comingSoon,
  comingSoonLabel = 'Coming soon',
  statusBadge,
  hotLabel = 'Hot',
  zoomLabel = 'Enlarge image',
  closeLabel = 'Close',
  waveRow,
  waveCol = 0,
  shineRow,
  shineCol = 0,
  card,
  silent = false,
}: GameTileProps) {
  const wave = waveRow != null;
  const shine = shineRow != null;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(imageUrl || FALLBACK_ICON);
  const [zoomOpen, setZoomOpen] = useState(false);
  const showStatusBadge = !!statusBadge && !comingSoon;
  const showHot = hot && !comingSoon && !showStatusBadge;
  const statusBadgeEl = showStatusBadge ? (
    <span className="game-tile-coming-soon-overlay game-tile-status-overlay" data-testid="game-tile-status-badge">
      {statusBadge}
    </span>
  ) : null;

  useEffect(() => {
    if (!imageUrl) return;
    setImgLoaded(false);
    setImgSrc(imageUrl);
  }, [imageUrl]);

  const handleActivate: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!onClick) return;
    if (!silent) playClickSound();
    onClick(e);
  };

  const hotEl = showHot ? (
    <span className={cx('game-tile-hot bg-action-danger font-ui text-on-danger', card ? 'z-20' : 'z-10')}>{hotLabel}</span>
  ) : null;
  const balanceEl =
    typeof balance === 'number' ? (
      <p className="min-w-0 w-full max-w-[var(--gi)] truncate text-center">
        <Money amount={balance} direction="out" size="small" />
      </p>
    ) : null;
  const footerEl = footer ? <div className="min-w-0 w-full text-center">{footer}</div> : null;
  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined = onClick
    ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }
    : undefined;
  const waveVars = wave ? ({ '--wave-row': waveRow, '--wave-col': waveCol } as React.CSSProperties) : undefined;

  if (card) {
    // The preview Dialog sits beside the tile, not inside it: portal events bubble through the
    // React tree, so clicks and keys in the dialog would otherwise also activate the tile.
    return (
      <>
        <div
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          onClick={handleActivate}
          onKeyDown={onKeyDown}
          className={cx(
            // The card frame takes the style's card treatment (k-card), like Card.
            'relative flex min-w-0 flex-col items-center gap-1 rounded-container border k-card bg-card p-3 transition-colors duration-(--k-dur-fast)',
            onClick && cx('cursor-pointer hover:border-edge-strong', focusRing),
            wave && 'game-tile-wave',
            comingSoon && 'game-tile--coming-soon',
            className
          )}
          style={waveVars}
        >
          <div className="flex w-full flex-col gap-2">
            <p className="wf-games-page-tip truncate font-ui text-body font-semibold text-primary" data-tooltip={name}>
              {name}
            </p>
            <div className="relative isolate aspect-square w-full overflow-hidden rounded-control bg-raised">
              {imageUrl ? <CardArt imageUrl={imageUrl} name={name} /> : <NebulaArt name={name} className="absolute inset-0" />}
              {comingSoon ? (
                <span className="game-tile-coming-soon-overlay" aria-hidden>
                  {comingSoonLabel}
                </span>
              ) : null}
              {statusBadgeEl}
              {hotEl}
              {imageUrl && !comingSoon && (
                <IconButton
                  label={zoomLabel}
                  title={zoomLabel}
                  icon={<ZoomIn />}
                  size="sm"
                  silent
                  className="absolute bottom-2.5 right-2.5 z-20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomOpen(true);
                  }}
                />
              )}
            </div>
          </div>
          {balanceEl}
          {footerEl}
        </div>
        {imageUrl && (
          <Dialog open={zoomOpen} onClose={() => setZoomOpen(false)} title={name} size="lg" closeLabel={closeLabel}>
            <img
              src={imageUrl}
              alt={name}
              className="mx-auto block w-full max-w-md rounded-container object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = FALLBACK_ICON;
              }}
            />
          </Dialog>
        )}
      </>
    );
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleActivate}
      onKeyDown={onKeyDown}
      className={cx(
        'relative flex flex-col items-center gap-1 shrink-0 min-w-0',
        'w-[var(--gi)] max-w-[var(--gi)]',
        onClick && cx('cursor-pointer', focusRing),
        wave && 'game-tile-wave',
        shine && 'game-tile-shine',
        comingSoon && 'game-tile--coming-soon',
        className
      )}
      style={{
        ...waveVars,
        ...(shine ? ({ '--shine-row': shineRow, '--shine-col': shineCol } as React.CSSProperties) : undefined),
      }}
    >
      {/* isolate: the badge z-index stays inside the art, under page layers and the picker tick */}
      <div className="lobby-gi-ico relative isolate h-[var(--gi)] w-[var(--gi)] shrink-0 overflow-hidden">
        {imageUrl ? (
          <>
            {!imgLoaded && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
            <img
              ref={(el) => {
                if (el?.complete && el.naturalWidth > 0) setImgLoaded(true);
              }}
              src={imgSrc}
              alt={name}
              loading="eager"
              decoding="async"
              draggable={false}
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                if (imgSrc !== FALLBACK_ICON) {
                  setImgSrc(FALLBACK_ICON);
                  return;
                }
                setImgLoaded(true);
              }}
              className={cx(
                'block w-full h-full object-cover pointer-events-none select-none',
                !imgLoaded && 'opacity-0'
              )}
            />
          </>
        ) : (
          <NebulaArt name={name} />
        )}
        {comingSoon ? (
          <span className="game-tile-coming-soon-overlay" aria-hidden>
            {comingSoonLabel}
          </span>
        ) : null}
        {statusBadgeEl}
        {hotEl}
      </div>
      <p
        className="min-w-0 w-full max-w-[var(--gi)] truncate px-0.5 text-center font-ui text-small font-bold leading-snug text-primary"
        title={name}
      >
        {name}
      </p>
      {balanceEl}
      {footerEl}
    </div>
  );
}
